/* import __COLOCATED_TEMPLATE__ from './analytics-table.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { convertToUTCDate } from "../../../utils/date-and-time";

const WIDE_SIZE = 2;

class AnalyticsTable extends Component {
  @service moment;
  @service session;

  @tracked sortColumnIdx = this.defaultSortIdx;
  @tracked sortDescending = this.defaultSortDescending;

  get columnUnitSize() {
    const columns = this.args.columns;
    const numUnits = columns.reduce((count, column) => {
      if (column.hidden) {
        return count;
      }

      if (column.wide) {
        return count + WIDE_SIZE;
      } else {
        return count + 1;
      }
    }, 0);

    return 100 / numUnits;
  }

  get thinStyle() {
    const unitSize = this.columnUnitSize;
    return htmlSafe(`width: ${(unitSize)}%;`);
  }

  get wideStyle() {
    const unitSize = this.columnUnitSize;
    return htmlSafe(`width: ${(unitSize * WIDE_SIZE)}%;`);
  }

  get tableHeaders() {
    return this.args.columns.map(column => {
      const {label, wide, rightAlign} = column;
      const style = wide ? this.wideStyle : this.thinStyle;

      return {
        label,
        style,
        rightAlign,
      };
    });
  }

  get hiddenColumnIndices() {
    return this.args.columns.reduce((hidden, column, idx) => {
      if (column.hidden) {
        hidden.push(idx);
      }

      return hidden;
    },[]);
  }

  get formattedData() {
    return this._formatData(true);
  }

  get csvFormattedData() {
    return this._formatData(false);
  }

  get csvSortedData() {
    const data = this.csvFormattedData;
    const sortIdx = this.defaultSortIdx;
    const descending = this.defaultSortDescending;

    return this._sortDataByColumnIdxAndDirection(data, sortIdx, descending);
  }

  /**
   * Url to get the csv file for a given year.
   *
   * @type {String}
   */
  get _analyticsTableDownloadUrl() {
    let startAtDate = this.moment.moment({
      year: this.args.currentYear,
      month: 0,
      day: 1
    });

    let endDate = this.moment.moment({
      year: this.args.currentYear,
      month: 11,
      day: 31,
      hours: 23,
      minutes: 59,
      seconds: 59,
      milliseconds: 59
    });

    startAtDate = convertToUTCDate(startAtDate.toDate());
    endDate = convertToUTCDate(endDate.toDate());

    return `/analytics/projects/csv?company_id=${this.session.currentUser.company.id}&start_at=${startAtDate.toDate().toISOString()}&end_at=${endDate.toDate().toISOString()}`;
  }

  _formatData(useFilters = false) {
    const columns = this.args.columns;
    let data = this.args.tableData;

    // No data
    if (!data || Object.keys(data).length < 1) {
      return [];
    }

    // FILTERING
    if (useFilters) {
      const filters = this.args.filters;
      data = data.filter(item => filters.every(filter => filter(item)) );
    }

    // FORMATTING
    return data.map(item => {
      return columns.map(column => {
        const {param, wide, display, link, action, icon, isAllowHtml, rightAlign} = column;
        const value = item.get(param);
        const style = wide ? this.wideStyle : this.thinStyle;

        const displayValue = (typeof display === 'function') ? display(value) : value;
        const linkValue = (typeof link === 'function') ? link(value, item) : null;

        return {
          value,
          isAllowHtml: isAllowHtml,
          display: displayValue || null,
          link: linkValue,
          style,
          action,
          icon,
          rightAlign,
        };
      });
    });
  }

  get sortFunction() {
    return this._sortFunctionForIdx(this.sortColumnIdx);
  }

  get defaultSortIdx() {
    const columns = this.args.columns;
    const defaultIdx = columns.findIndex(col => col.defaultSort);
    // default to 0 if their isn't a default sort function
    return Math.max(0, defaultIdx);
  }

  get defaultSortDescending() {
    const columns = this.args.columns;
    const idx = this.defaultSortIdx;

    return columns[idx].sortDescending;
  }

  // all this data gets put in the DOM so this can take pretty long time with big datasets
  get sortedData() {
    const data = this.formattedData;

    if (!data || Object.keys(data).length < 1) {
      // Nothing to do
      return;
    }

    return this._sortDataByColumnIdxAndDirection(data, this.sortColumnIdx, this.sortDescending);
  }

  _sortDataByColumnIdxAndDirection(data, idx, sortDescending) {
    let formattedData = Array.from(data);
    const sortFunction = this._sortFunctionForIdx(idx);

    formattedData.sort(sortFunction);
    if (sortDescending) {
      formattedData.reverse();
    }

    return formattedData;
  }

  @action
  toggleSortDirection() {
    this.sortDescending = !this.sortDescending;
  }

  @action
  setSortColumnIdx(idx) {
    // if we are already using this column then toggle direction
    if (idx === this.sortColumnIdx) {
      this.toggleSortDirection();
    } else {
      this.sortColumnIdx = idx;
      this.sortDescending = Boolean(this.args.columns[idx].sortDescending);
    }
  }

  _sortFunctionForIdx(idx) {
    const columns = this.args.columns;
    const {sortFunction} = columns[idx];
    const stringSort = this._stringSort;

    if (typeof sortFunction === 'function') {
      return (a, b) => sortFunction(a[idx].value, b[idx].value);
    }

    // default sort function
    return (a, b) => {
      const A = a[idx].value;
      const B = b[idx].value;

      if (A === B) {
        return 0;
      }

      if (!A) {
        return -1;
      }

      if (!B) {
        return 1;
      }

      if ((typeof A === 'string') || (typeof B === 'string')) {
        return stringSort(A, B);
      }

      return A < B ? -1 : 1;
    };
  }

  _stringSort(a, b) {
    const A = String(a).toUpperCase().trim();
    const B = String(b).toUpperCase().trim();

    if (A === B) {
      return 0;
    }

    return A < B ? -1 : 1;
  }
}

export default AnalyticsTable;
